import React, { useEffect, useState } from "react";
import { FaGlobe, FaDollarSign } from 'react-icons/fa';
import DebugService from "../services/DebugService";
import {
    CONST_SITE_LABEL,
    PLATFORM,
    STORAGE_KEYS,
    setScopedItem
} from "../components/common/Helper";
import SnackbarNotification, { SNACKBAR_THEME } from "../components/common/SnackbarNotification";
import '../components/common/Styles.css';
import BillingApiService from "../services/BillingApiService";
import CustomTable from "../components/common/Table.jsx";
import dayjs from "dayjs";
import { useAuth } from "../state/AuthContext";
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage";

export default function DataTransfer() {
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate());
    const last3Years = new Date(currentDate.getFullYear() - 3, currentDate.getMonth(), currentDate.getDate());

    const [data, setData] = useState([]);
    const [totalPriceValue, setTotalPriceValue] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(null);
    const [snackbarStatus, setSnackbarStatus] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState([]);
    const [selectedMonthYear, setSelectedMonthYear] = useState(dayjs(lastMonth));
    const [dataTransferApiData, setDataTransferApiData] = useState({});

    const { selectedAccount, selectedPlatform } = useAuth();
    const { getScopedItem } = useScopedLocalStorage();

    const fetchData = () => {
        setIsLoading(true);

        if (selectedPlatform === PLATFORM.AZURE) {
            const dataRequest = {
                type: 'DATA_TRANSFER_COST',
                userAccountId: selectedAccount,
                selectedMonthYear: selectedMonthYear.format('MMMM YYYY')
            };
            BillingApiService.getAzureDataTransferData(dataRequest)
                .then(res => {
                    DebugService.log(`Azure Data Transfer Component: API response`, res);
                    setDataTransferApiData(res?.data ? res : {});
                    const apiData = res['data'][0]['locations'] ?? [];
                    setData(apiData);
                    setTotalPriceValue(res['totalMonthlyCost'] ?? {});

                    if (res['error']) {
                        DebugService.error(`Azure Data Transfer Component: Error caught while fetching data`, res['error']);
                    }

                    setIsLoading(false);
                })
                .catch(err => {
                    DebugService.error(`Azure Data Transfer Component: Error caught while fetching data`, err);
                    setIsLoading(false);
                });
        } else {
            BillingApiService.getDataTransferData({ selectedAccount, selectedMonthYear: selectedMonthYear.format('MMMM YYYY') })
                .then(res => {
                    DebugService.log(`Data Transfer Component: API response`, res);
                    setDataTransferApiData(res?.data ? res : {});
                    const apiData = res['data']['regions'] ?? [];
                    setData(apiData);
                    setTotalPriceValue(res['totalPriceValue'] ?? {});

                    if (res['error']) {
                        DebugService.error(`Data Transfer Component: Error caught while fetching data`, res['error']);
                    }

                    setIsLoading(false);
                })
                .catch(err => {
                    DebugService.error(`Data Transfer Component: Error caught while fetching data`, err);
                    setIsLoading(false);
                });
        }
    };

    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Data Transfer`;
    }, []);

    useEffect(() => {
        fetchData();
    }, [selectedMonthYear, selectedAccount]);

    useEffect(() => {
        setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, dataTransferApiData);
    }, [dataTransferApiData]);

    const handleRegionChange = (region) => {
        const regionKey = `${region['region'] ?? region['location']}`;

        if (selectedRegion.includes(regionKey)) {
            setSelectedRegion(selectedRegion.filter(r => r !== regionKey));
        } else {
            setSelectedRegion(prev => ([...prev, regionKey]));
        }
    };

    return (
        <div style={{ padding: '24px 28px 48px', background: '#f4f6fa', minHeight: '100vh' }}>
            <style>{`
                @keyframes dt-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                .stats-data-transfer {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                    margin-bottom: 20px;
                    width: 100%;
                }
                .stat-card {
                    border-radius: 12px;
                    padding: 12px 18px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 14px rgba(16,24,40,.05);
                    border: 1px solid rgba(0,0,0,.02);
                }
                .stat-card .ico {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }
                .stat-card .lab {
                    font-size: 12px;
                    color: #6b7280;
                    font-weight: 600;
                }
                .stat-card .num {
                    font-size: 24px;
                    font-weight: 750;
                    letter-spacing: -.02em;
                    margin-top: 1px;
                }
                .s-reg { background: #e8f1fb; }
                .s-reg .ico { background: #d4e6fb; color: #3b82f6; }
                .s-reg .num { color: #1d4ed8; }
                .s-cost { background: #fbe9ec; }
                .s-cost .ico { background: #f8d4da; color: #ef4444; }
                .s-cost .num { color: #1d4ed8; }
                .region-list-dt {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    width: 100%;
                }
                .region-item {
                    background: #ffffff;
                    border: 1px solid #e6e9f0;
                    border-radius: 14px;
                    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 14px rgba(16,24,40,.05);
                    overflow: hidden;
                }
                .region-row-dt {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 18px 22px;
                    cursor: pointer;
                    user-select: none;
                }
                .region-row-dt:hover { background: #f8fafc; }
                .exp-btn {
                    width: 26px;
                    height: 26px;
                    border-radius: 7px;
                    border: 1.5px solid #3b82f6;
                    color: #3b82f6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    font-weight: 700;
                    flex-shrink: 0;
                    transition: transform .18s;
                }
                .region-item.open .exp-btn { transform: rotate(45deg); }
                .region-name-dt { font-size: 15.5px; font-weight: 600; flex: 1; }
                .region-bar-dt {
                    width: 160px;
                    height: 7px;
                    border-radius: 999px;
                    background: #edf1f6;
                    overflow: hidden;
                }
                .region-bar-dt .fill {
                    height: 100%;
                    border-radius: 999px;
                    background: linear-gradient(90deg, #f5821f, #fbbf24);
                }
                .region-cost-dt {
                    font-size: 16px;
                    font-weight: 750;
                    color: #1d4ed8;
                    min-width: 80px;
                    text-align: right;
                }
                .region-cost-dt.zero { color: #6b7280; }
                .region-detail-dt {
                    padding: 0 22px 18px 64px;
                    color: #6b7280;
                    font-size: 13px;
                }
                .region-detail-dt .empty-dt {
                    padding: 12px 0;
                    border-top: 1px dashed #e6e9f0;
                }
                .dt-skeleton {
                    height: 62px;
                    border-radius: 14px;
                    background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
                    background-size: 200% 100%;
                    animation: dt-shimmer 1.5s infinite;
                }
                @media (max-width:980px){ .region-bar-dt{display:none} }
            `}</style>

            {/* Filter bar */}
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 26 }}>
                <div style={{ position: 'relative', background: '#fff', border: '1px solid #e6e9f0', borderRadius: 12, padding: '10px 40px 10px 16px', minWidth: 200 }}>
                    <span style={{ position: 'absolute', top: -7, left: 12, background: '#fff', padding: '0 5px', fontSize: 10.5, color: '#6b7280' }}>Month / Year</span>
                    <input
                        type="month"
                        value={selectedMonthYear.format('YYYY-MM')}
                        min={dayjs(last3Years).format('YYYY-MM')}
                        max={dayjs(currentDate).format('YYYY-MM')}
                        onChange={(e) => setSelectedMonthYear(dayjs(e.target.value + '-01'))}
                        style={{ border: 'none', outline: 'none', fontSize: 14, fontWeight: 600, color: '#1f2937', background: 'transparent', width: '100%' }}
                    />
                </div>
            </div>

            {/* Stat cards */}
            <div className="stats-data-transfer">
                <div className="stat-card s-reg">
                    <div className="ico"><FaGlobe /></div>
                    <div>
                        <div className="num">{data.length}</div>
                        <div className="lab">Total Regions</div>
                    </div>
                </div>
                <div className="stat-card s-cost">
                    <div className="ico"><FaDollarSign /></div>
                    <div>
                        <div className="num">${parseFloat(totalPriceValue['totalServicesCostValue'] ?? 0).toFixed(2)}</div>
                        <div className="lab">Total Services Cost</div>
                    </div>
                </div>
            </div>

            {/* Region list */}
            {isLoading ? (
                <div className="region-list-dt">
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="dt-skeleton" />
                    ))}
                </div>
            ) : (
                <div className="region-list-dt">
                    {data
                        .sort((a, b) => parseFloat(b['cost'] ?? 0) - parseFloat(a['cost'] ?? 0))
                        .map((region, regionIndex) => {
                            const regionKey = region['region'] ?? region['location'];
                            const isOpen = selectedRegion.includes(regionKey);
                            const costVal = parseFloat(region['cost'] ?? 0);
                            const maxCost = Math.max(...data.map(r => parseFloat(r['cost'] ?? 0)), 1);
                            const fillWidth = `${(costVal / maxCost) * 100}%`;

                            return (
                                <div key={regionIndex} className={`region-item ${isOpen ? 'open' : ''}`}>
                                    <div className="region-row-dt" onClick={() => handleRegionChange(region)}>
                                        <span className="exp-btn">+</span>
                                        <span className="region-name-dt">{regionKey}</span>
                                        <span className={`region-cost-dt ${costVal === 0 ? 'zero' : ''}`}>
                                            ${costVal.toFixed(2)}
                                        </span>
                                    </div>

                                    {isOpen && (
                                        <div className="region-detail-dt">
                                            {costVal === 0 ? (
                                                <div className="empty-dt">
                                                    No data-transfer cost recorded for {regionKey} in {selectedMonthYear.format('MMMM YYYY')}.
                                                </div>
                                            ) : selectedPlatform === PLATFORM.AWS ? (
                                                <CustomTable
                                                    rows={(region['usageTypes'] ?? []).map((ut, i) => ({
                                                        id: `${regionKey}-${i}`,
                                                        description: `$${ut['unitCost'] ?? 0.00} per ${ut['unitType'] || 'GB'} of ${ut['usageType']}`,
                                                        usageQuantity: `${ut['usage']} ${ut['unitType'] || 'GB'}`,
                                                        amount: `$${ut['cost']}`
                                                    }))}
                                                    columns={[
                                                        { key: 'description', label: 'Description', render: (row) => <span style={{ display: 'block', textAlign: 'left' }}>{row.description}</span> },
                                                        { key: 'usageQuantity', label: 'Usage Quantity' },
                                                        { key: 'amount', label: 'Amount', className: 'num' }
                                                    ]}
                                                    showToolbar={false}
                                                    emptyText="No details found."
                                                    subscriptionGated
                                                />
                                            ) : (
                                                <CustomTable
                                                    rows={(region['usageTypes'] ?? []).map((ut, i) => ({
                                                        ...ut,
                                                        id: `${regionKey}-${i}`,
                                                        cost: `$${ut['cost']?.toFixed(2) ?? '0.00'}`,
                                                        usage: `${ut['usage']?.toFixed(2)}`
                                                    }))}
                                                    columns={[
                                                        { key: 'cost', label: 'Cost', className: 'num' },
                                                        { key: 'usage', label: 'Usage', className: 'num' },
                                                        { key: 'tier', label: 'Tier' },
                                                        { key: 'product', label: 'Product' },
                                                        { key: 'meter', label: 'Meter' },
                                                        { key: 'chargeType', label: 'Charge' }
                                                    ]}
                                                    showToolbar={false}
                                                    emptyText="No details found."
                                                    subscriptionGated
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>
            )}

            {snackbarStatus && <SnackbarNotification
                initialOpen={true}
                duration={5000}
                message={snackbarMessage}
                actionButtonName={'Ok'}
                theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
                yPosition={'top'}
                xPosition={'center'}
            />}
        </div>
    );
}
